<template>
  <div class="min-h-screen">
    <SiteHeader :page-name="'Subdivision Dashboard'" :unsaved />
    <template v-if="subdivision">
      <Card pt:body:class="pt-0!">
        <template #content>
          <div class="flex lg:flex-row flex-col justify-between gap-5">
            <div class="flex flex-col w-full lg:w-1/4 justify-start gap-3">
              <div class="flex flex-col gap-3 mt-3">
                <div class="flex justify-between gap-2">
                  <h1 class="text-4xl font-bold mb-3">{{ subdivision.subdivisionName }}</h1>
                  <Chip icon="pi pi-users" class="h-10 font-bold min-w-fit" :label="subdivision.userGroup.name"></Chip>
                </div>
                <Button
                  v-if="userPermissions.administrator"
                  label="Update Name/Group"
                  icon="pi pi-pencil"
                  class="h-10 -mt-3 w-full"
                  @click="startEditingName"
                ></Button>
                <div class="flex flex-col gap-1">
                  <label class="font-bold">Maturity Date</label
                  ><DatePicker
                    manualInput
                    showIcon
                    show-button-bar
                    iconDisplay="input"
                    inputId="modelField"
                    class="h-10"
                    inputClass="w-36"
                    panelClass="bg-white dark:bg-surface-700/80 backdrop-blur-xl"
                    v-model="subdivision.maturityDate"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <Button label="Paydowns" icon="pi pi-arrow-circle-down" class="w-full h-10" @click="editPaydowns = true"></Button>

                  <ReportsListButton context="SINGLE_SUBDIVISION" classes="h-10" :subdivision-id="subdivision.subdivisionId"></ReportsListButton>
                </div>
                <SubmitButton class="mt-5" @click="submitSubdivision"></SubmitButton>
              </div>
            </div>
            <Card class="mt-3 w-full lg:w-1/4" pt:content:class="p-0">
              <template #content>
                <div class="flex flex-col gap-1">
                  <div class="flex flex-col">
                    <span class="font-bold text-nowrap">Spec Limit</span>
                    <InputGroup pt:root:class="flex items-stretch h-10">
                      <InputNumber v-model="subdivision.specLimit" input-class="rounded-l-lg!"></InputNumber>
                      <InputGroupAddon
                        ><span :class="specCount > subdivision.specLimit ? 'text-red-500' : 'text-green-500'">{{ specCount }}</span></InputGroupAddon
                      >
                    </InputGroup>
                  </div>
                  <div class="flex flex-col">
                    <span class="font-bold text-nowrap">Model Limit</span>
                    <InputGroup pt:root:class="flex items-stretch h-10">
                      <InputNumber v-model="subdivision.modelLimit" input-class="rounded-l-lg!"></InputNumber>
                      <InputGroupAddon
                        ><span :class="modelCount > subdivision.modelLimit ? 'text-red-500' : 'text-green-500'">{{
                          modelCount
                        }}</span></InputGroupAddon
                      >
                    </InputGroup>
                  </div>
                  <div class="flex flex-col">
                    <span class="font-bold text-nowrap">Pre-Sold Limit</span>
                    <InputGroup pt:root:class="flex items-stretch h-10">
                      <InputNumber v-model="subdivision.preSoldLimit" input-class="rounded-l-lg!"></InputNumber>
                      <InputGroupAddon
                        ><span :class="preSoldCount > subdivision.preSoldLimit ? 'text-red-500' : 'text-green-500'">{{
                          preSoldCount
                        }}</span></InputGroupAddon
                      >
                    </InputGroup>
                  </div>
                </div>
              </template>
            </Card>
            <Card class="mt-3 w-full lg:w-1/4" pt:body:class="h-full" pt:content:class="h-full">
              <template #content>
                <div class="flex flex-col gap-2 justify-between h-full">
                  <div class="flex flex-col gap-1">
                    <label class="font-bold">Commitment</label>
                    <InputNumber v-model="subdivision.netCost" mode="currency" currency="USD" :allow-empty="false"></InputNumber>
                  </div>
                  <div class="flex justify-between">
                    <div class="flex flex-col gap-1">
                      <p>Commited</p>
                      <span class="font-bold text-xl">{{ formatCurrency(totalCommited) }}</span>
                    </div>
                    <div class="flex flex-col gap-1">
                      <p>Available</p>
                      <span class="font-bold text-xl" :class="{ 'text-red-500': totalAvailable < 0 }">{{ formatCurrency(totalAvailable) }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </Card>
            <Card class="mt-3 w-full lg:w-1/4" pt:body:class="h-full" pt:content:class="h-full">
              <template #content>
                <div class="flex flex-col gap-2 justify-between h-full">
                  <div class="flex flex-col gap-1">
                    <label class="font-bold">Available Balance</label>
                    <InputNumber v-model="subdivision.creditLine" mode="currency" currency="USD" :allow-empty="false"></InputNumber>
                  </div>
                  <div class="flex justify-between">
                    <div class="flex flex-col gap-1">
                      <p>Principal Bal.</p>
                      <span class="font-bold text-xl">{{ formatCurrency(principalBalance) }}</span>
                    </div>
                    <div class="flex flex-col gap-1">
                      <p>Funds Avail.</p>
                      <span class="font-bold text-xl" :class="{ 'text-red-500': fundsAvailable < 0 }">{{ formatCurrency(fundsAvailable) }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </Card>
          </div>
        </template></Card
      >
      <SubdivisionProjectsTable ref="projectTable" v-model:subdivision="subdivision" :draw-totals="subdivision.drawTotals" />
      <TSDialog
        v-model:visible="editPaydowns"
        label="Subdivision Paydowns"
        icon="arrow-circle-down"
        v-on:after-hide="editPaydowns = false"
        size="w-[1200px]!"
      >
        <template #content>
          <SubdivisionPayDowns v-model:subdivision="subdivision" v-model:paydownsEdited="paydownsEdited" />
        </template>
      </TSDialog>
      <TSDialog v-model:visible="editName" label="Update Name/Group" icon="pencil" v-on:after-hide="editPaydowns = false" size="w-[800px]!">
        <template #content>
          <div class="flex flex-row gap-3 mb-3">
            <StackedLabelInputNoForm v-model="newName" name="newSubdivisionName" label="Name" class="basis-1/2"></StackedLabelInputNoForm>
            <StackedLabelDropdownNoForm
              v-model="newGroup"
              name="userGroup"
              label="User Group"
              size="basis-1/2"
              :options="userGroups"
              options-label="name"
              placeholder="Select a user group"
              :disabled="userGroups.length <= 1"
            />
          </div>
          <p class="text-warning-500 text-center text-sm font-bold">
            ***Warning: changing the user group will change all project user groups and effect their ledgers***
          </p>
          <div class="flex justify-end gap-2 mt-2">
            <SubmitButton label="Confirm" type="button" @click="tryConfirmNameChange" />
          </div>
        </template>
      </TSDialog>
    </template>
    <template v-else>
      <div class="flex flex-col w-full mt-20">
        <ProgressSpinner style="width: 100px; height: 100px" class="fill-surface-0 dark:fill-surface-800 mx-auto" animationDuration=".5s" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import SubdivisionPayDowns from "@/components/subdivisions/SubdivisionPayDowns.vue";
import SubdivisionProjectsTable from "@/components/subdivisions/SubdivisionProjectsTable.vue";
import { ProjectStatusCategory } from "@/enums/ProjectStatusCategory";
import { getSubdivisonColumnDefault } from "@/enums/SubdivisionColumns";
import { getUserPermissions } from "@/services/Permissions";
import { error, errorApi, success } from "@/services/ToastService";
import { formatCurrency, getUserGroups, handleApiResponse } from "@/services/Utility";
import { useAuthStore } from "@/stores/auth";
import { CodeSchema, type Code } from "@/types/Codes";
import type { ApiError } from "@/types/Response";
import { SubdivisionPaydownSchema, SubdivisionSchema, type Subdivision } from "@/types/Subdivision";
import { UserPermissionsSchema, type UserPermissions } from "@/types/User";
import axios from "axios";
import { computed, onBeforeMount, ref } from "vue";
import { useRoute } from "vue-router";
import { z } from "zod";

const route = useRoute();
const editName = ref<boolean>(false);
const editPaydowns = ref<boolean>(false);
const subdivision = ref<Subdivision>();
const paydownsEdited = ref(false);
const userStore = useAuthStore();
const userPermissions = ref<UserPermissions>(UserPermissionsSchema.parse({}));
const newName = ref<string>("");
const newGroup = ref();
const userGroups = ref<Code[]>([]);
const projectTable = ref();

const createSubdivisionEditRequest = (): SubdivisionEditRequest => {
  if (subdivision.value == null) {
    return {
      subdivisionId: "",
      maturityDate: new Date(),
      specLimit: 0,
      modelLimit: 0,
      preSoldLimit: 0,
      creditLine: 0,
      netCost: 0,
      paydowns: [],
      name: "",
      userGroup: null,
      subdivisionColumns: null,
      subdivisionColumnOrder: [],
    };
  }

  return {
    subdivisionId: subdivision.value.subdivisionId,
    maturityDate: subdivision.value.maturityDate,
    specLimit: subdivision.value.specLimit,
    modelLimit: subdivision.value.modelLimit,
    preSoldLimit: subdivision.value.preSoldLimit,
    netCost: subdivision.value.netCost,
    creditLine: subdivision.value.creditLine,
    paydowns: subdivision.value.paydowns,
    name: subdivision.value.subdivisionName,
    userGroup: subdivision.value.userGroup,
    subdivisionColumns: Number(subdivision.value.subdivisionColumns),
    subdivisionColumnOrder: subdivision.value.subdivisionColumnOrder,
  };
};

const originalSubdivision = ref<SubdivisionEditRequest>(createSubdivisionEditRequest());

const unsaved = computed<boolean>(() => {
  return (
    subdivision.value != null &&
    (originalSubdivision.value.subdivisionId !== subdivision.value.subdivisionId ||
      originalSubdivision.value.maturityDate !== subdivision.value.maturityDate ||
      originalSubdivision.value.specLimit !== subdivision.value.specLimit ||
      originalSubdivision.value.modelLimit !== subdivision.value.modelLimit ||
      originalSubdivision.value.preSoldLimit !== subdivision.value.preSoldLimit ||
      originalSubdivision.value.netCost !== subdivision.value.netCost ||
      originalSubdivision.value.creditLine !== subdivision.value.creditLine ||
      paydownsEdited.value)
  );
});

const SubdivisionEditRequestSchema = z.object({
  subdivisionId: z.string(),
  specLimit: z.number(),
  modelLimit: z.number(),
  preSoldLimit: z.number(),
  netCost: z.number(),
  creditLine: z.number(),
  maturityDate: z.coerce.date().nullable(),
  paydowns: z.array(SubdivisionPaydownSchema),
  name: z.string(),
  userGroup: CodeSchema.nullable(),
  subdivisionColumns: z.number().nullable(),
  subdivisionColumnOrder: z.array(z.number()),
});

type SubdivisionEditRequest = z.infer<typeof SubdivisionEditRequestSchema>;

const specCount = computed<number>(() => {
  if (subdivision.value) {
    return subdivision.value.drawTotals.filter(
      (dt) => dt.projectStatus.category === ProjectStatusCategory.ACTIVE && dt.propertyType.startsWith("SPEC")
    ).length;
  }
  return 0;
});

const modelCount = computed<number>(() => {
  if (subdivision.value) {
    return subdivision.value.drawTotals.filter(
      (dt) => dt.projectStatus.category === ProjectStatusCategory.ACTIVE && dt.propertyType.startsWith("MODEL")
    ).length;
  }
  return 0;
});

const preSoldCount = computed<number>(() => {
  if (subdivision.value) {
    return subdivision.value.drawTotals.filter(
      (dt) => dt.projectStatus.category === ProjectStatusCategory.ACTIVE && dt.propertyType.startsWith("PRESOLD")
    ).length;
  }
  return 0;
});

const totalCommited = computed<number>(() => {
  if (subdivision.value) {
    return subdivision.value.drawTotals
      .filter((dt) => dt.projectStatus.category === ProjectStatusCategory.ACTIVE)
      .map((item) => item.totalCreditCommitment)
      .reduce((sum, current) => sum + current, 0);
  }
  return 0;
});

const totalAvailable = computed<number>(() => {
  if (subdivision.value) {
    return (
      subdivision.value?.netCost -
      subdivision.value.drawTotals
        .filter((dt) => dt.projectStatus.category === ProjectStatusCategory.ACTIVE)
        .map((total) => total.totalCreditCommitment)
        .reduce((sum, current) => sum + current, 0)
    );
  }
  return 0;
});

const paydownTotal = computed<number>(() => {
  if (subdivision.value) {
    return subdivision.value.paydowns.map((paydown) => paydown.amount).reduce((sum, current) => sum + current, 0);
  }
  return 0;
});

const principalBalance = computed<number>(() => {
  if (subdivision.value) {
    return subdivision.value.totalPrincipalBalance - paydownTotal.value;
  }
  return 0;
});

const fundsAvailable = computed<number>(() => {
  if (subdivision.value) {
    return subdivision.value.creditLine - principalBalance.value;
  }
  return 0;
});

onBeforeMount(async (): Promise<void> => {
  await getUserPermissions(userStore.getUserInfo().userId).then((result: UserPermissions) => (userPermissions.value = result));
  await getSubdivisionDetails();
  await getUserGroups(true, true).then((codes: Code[]) => (userGroups.value = codes));
  if (userGroups.value.length == 1) newGroup.value = userGroups.value[0];
});

const getSubdivisionDetails = async (): Promise<void> => {
  await axios
    .get(`/subdivision/${route.params.id}`)
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          subdivision.value = SubdivisionSchema.parse(data);
          if (subdivision.value.subdivisionColumns == null) subdivision.value.subdivisionColumns = getSubdivisonColumnDefault();
          originalSubdivision.value = createSubdivisionEditRequest();
        },
        (errors: ApiError[]) => error("An error occurred", errors[0].message)
      )
    )
    .catch((e) => errorApi(e));
};

const submitSubdivision = (): void => {
  if (!subdivision.value) return;

  const request: SubdivisionEditRequest = {
    subdivisionId: subdivision.value.subdivisionId,
    maturityDate: subdivision.value.maturityDate,
    specLimit: subdivision.value.specLimit,
    modelLimit: subdivision.value.modelLimit,
    preSoldLimit: subdivision.value.preSoldLimit,
    netCost: subdivision.value.netCost,
    creditLine: subdivision.value.creditLine,
    paydowns: subdivision.value.paydowns,
    name: subdivision.value.subdivisionName,
    userGroup: subdivision.value.userGroup,
    subdivisionColumns: Number(subdivision.value.subdivisionColumns),
    subdivisionColumnOrder: projectTable.value.getColumnOrder(),
  };

  axios
    .post("/subdivision/edit", request)
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          originalSubdivision.value = request;
          paydownsEdited.value = false;
          success("Success", "Subdivision info updated");
        },
        (errors: ApiError[]) => error("An error occurred", errors[0].message)
      )
    )
    .catch((e) => errorApi(e));
};

const startEditingName = (): void => {
  if (subdivision.value == null) return;
  newName.value = subdivision.value.subdivisionName;
  newGroup.value = subdivision.value.userGroup;
  editName.value = true;
};

const tryConfirmNameChange = (): void => {
  if (newName.value === "") error("Invalid Name", "Subdivision name may not be blank");

  if (subdivision.value) {
    subdivision.value.subdivisionName = newName.value;
    subdivision.value.userGroup = newGroup.value;
  }

  editName.value = false;
};
</script>
